import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { F, BRANDS, TABS, SECTIONS, SOURCES, makeData, brandBySlug, tabBySlug, brandFromAudit, FX_AS_OF, RANGE_LABEL } from '@/dashboard/data';
import { Dot, ShareModal, SheetsPanel, ClientPanel } from '@/dashboard/ui';
import { OverviewTab, PaidTab, EcomTab } from '@/dashboard/tabsCore';
import { OrganicTab, CRMTab, CompTab, BlindspotsTab } from '@/dashboard/tabsAnalytics';
import axios from 'axios';
import { toast } from 'sonner';
import { Zap, DollarSign, ShoppingCart, Leaf, Target, Eye, AlertTriangle, Sun, Moon, ChevronLeft, Menu, LogOut, Crown, Bot, FileText, Bell, Link2, Unlink, Sparkles } from 'lucide-react';
import PageSEO from '@/components/PageSEO';
import IntelligencePanel from '@/components/IntelligencePanel';

const ICON_MAP = { Zap, DollarSign, ShoppingCart, Leaf, Target, Eye, AlertTriangle };
const API = process.env.REACT_APP_BACKEND_URL;

export default function DashboardPage() {
  const { colors: T, isDark, toggleTheme } = useTheme();
  // Theme-aware accent for amber/OmniPlan text — darker on light mode for AA contrast.
  const AMBER = isDark ? '#FFD166' : '#B45309';
  const GREEN = isDark ? '#06D6A0' : '#047857';
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { brandSlug, tab: tabSlug } = useParams();
  const tab = (tabBySlug(tabSlug) || TABS[0]).id;
  const [dr, setDr] = useState('30d');
  const [data, setData] = useState(() => makeData('all', '30d'));
  const [loading, setLoading] = useState(false);
  const [showSheets, setShowSheets] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showClient, setShowClient] = useState(false);
  const [showSources, setShowSources] = useState(false);
  const [clientMode, setClientMode] = useState(false);
  const [clientVis, setClientVis] = useState(() => Object.fromEntries(SECTIONS.map(s => [s.id, true])));
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [oauthConns, setOauthConns] = useState([]);
  const [workspace, setWorkspace] = useState(null);
  const [showIntel, setShowIntel] = useState(false);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/org/me`, { withCredentials: true })
      .then(r => setWorkspace(r.data))
      .catch(e => console.error('Failed to load workspace:', e));
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/oauth/connections`, { withCredentials: true })
      .then(r => setOauthConns(r.data))
      .catch(e => console.error('Failed to load OAuth connections:', e));
  }, []); // API is module-level constant

  const realBrands = useMemo(() => (workspace?.brands || [])
    .map(b => brandFromAudit(b, b.audit)), [workspace]);
  const allBrands = useMemo(() => [...realBrands, ...BRANDS], [realBrands]);
  const activeBrand = useMemo(() =>
    allBrands.find(b => b.slug === brandSlug) || allBrands[0], [allBrands, brandSlug]);
  const brand = activeBrand?.id;
  const sub = workspace?.subscription;
  const activeTab = useMemo(() => TABS.find(t => t.id === tab) || TABS[0], [tab]);

  // Brand + tab live in the URL, so Share, Back/Forward and bookmarks all work.
  const dashPath = useCallback((bId, tId) => {
    const b = allBrands.find(x => x.id === bId) || allBrands[0];
    const t = TABS.find(x => x.id === tId) || TABS[0];
    return `/dashboard/${b?.slug || 'all-brands'}/${t.slug}`;
  }, [allBrands]);

  useEffect(() => {
    // Wait for the workspace: before it resolves we do not yet know the
    // user's own brand slugs, and rewriting the URL would lose a deep link.
    if (!workspace) return;
    const known = allBrands.find(b => b.slug === brandSlug);
    const canonical = `/dashboard/${(known || allBrands[0])?.slug || 'all-brands'}/${(tabBySlug(tabSlug) || TABS[0]).slug}`;
    if (`/dashboard/${brandSlug}/${tabSlug}` !== canonical) navigate(canonical, { replace: true });
  }, [workspace, brandSlug, tabSlug, allBrands, navigate]);

  useEffect(() => {
    if (!activeBrand) return;
    setLoading(true);
    const id = setTimeout(() => {
      setData(makeData(activeBrand.isReal ? activeBrand : activeBrand.id, dr));
      setLoading(false);
    }, 260);
    return () => clearTimeout(id);
  }, [activeBrand, dr]);

  const switchBrand = useCallback(id => navigate(dashPath(id, tab)), [navigate, dashPath, tab]);
  const switchDr = useCallback(d => setDr(d), []);
  const toggleCV = useCallback(id => setClientVis(v => ({ ...v, [id]: v[id] === false ? true : false })), []);
  const insights = useMemo(() => data.insights || [], [data.insights]);
  const liveSourceCount = useMemo(() => SOURCES.filter(s => s.ok).length, []);

  // Only pre-aggregated figures already on screen are sent to the analyst, so it
  // can explain them but has nothing to invent from.
  const intelContext = useMemo(() => ({
    brand: { name: activeBrand?.name, industry: activeBrand?.ind, market: activeBrand?.market,
             currency: activeBrand?.currency, business_model: activeBrand?.kind === 'b2b' ? 'b2b_leadgen' : 'd2c_ecommerce' },
    range: RANGE_LABEL[dr] || dr,
    active_tab: activeTab?.label,
    connected_sources: `${liveSourceCount} of ${SOURCES.length}`,
    disconnected_sources: SOURCES.filter(s => !s.ok).map(s => s.name),
    kpis: data.kpis,
    paid_channels: data.paid,
    ecom_platforms: data.ecom,
    monthly_last_6: (data.monthly || []).slice(-6),
    blind_spots: data.blindSpots,
    strong_spots: data.strongSpots,
    competitive: data.competitive,
    organic: data.organic,
    crm: activeBrand?.kind === 'b2b' ? data.crm : undefined,
  }), [activeBrand, dr, activeTab, data, liveSourceCount]);
  const intelSession = `${activeBrand?.slug || 'all'}-${tab}`;

  return (
    <div style={{ background: T.bg, minHeight: '100vh', fontFamily: F.head, color: T.text }}>
      <PageSEO title={`${activeBrand.name} Dashboard`} description="OmniView Command Center — Unified marketing dashboard with real-time KPIs, blind spots, and revenue recovery." keywords="marketing dashboard, ROAS, media analytics, OmniView" />
      {/* Ticker - clickable to Alerts */}
      <div data-testid="ticker-bar" onClick={() => navigate('/alerts')}
        style={{ background: T.tickerBg, borderBottom: `1px solid ${T.border}`, padding: '6px 0', overflow: 'hidden', position: 'relative', height: 36, cursor: 'pointer' }}>
        <div style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2, display: 'flex', alignItems: 'center', gap: 4,
          background: isDark ? 'rgba(255,68,68,.15)' : 'rgba(255,68,68,.08)', padding: '3px 8px', borderRadius: 4, border: '1px solid rgba(255,68,68,.2)' }}>
          <Bell size={12} color="#FF4444" />
          <span style={{ fontSize: 11, fontFamily: F.mono, fontWeight: 700, color: '#FF4444' }}>LIVE</span>
        </div>
        <div className="animate-ticker" style={{ display: 'flex', gap: 60, whiteSpace: 'nowrap', position: 'absolute', top: '50%', transform: 'translateY(-50%)', paddingLeft: 80 }}>
          {[0, 1].map(copy => (
            <div key={copy} aria-hidden={copy === 1} style={{ display: 'flex', gap: 60 }}>
              {insights.map((ins, i) => (
                <span key={i} style={{ fontSize: 14, fontFamily: F.mono, whiteSpace: 'nowrap', fontWeight: 600,
                  color: ins.startsWith('CRITICAL') ? '#FF4444' : ins.startsWith('ALERT') ? '#FFD166' : '#06D6A0' }}>{ins}</span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Nav */}
      <header style={{ background: T.navBg, borderBottom: `1px solid ${T.border}`, padding: '0 16px',
        position: 'sticky', top: 0, zIndex: 200, backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, padding: '10px 0', flexWrap: 'wrap' }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <button data-testid="back-to-home" onClick={() => navigate('/')}
              style={{ background: 'none', border: 'none', color: T.muted, cursor: 'pointer', padding: 2, display: 'flex' }}>
              <ChevronLeft size={18} />
            </button>
            <div style={{ width: 28, height: 28, background: 'linear-gradient(135deg,#002FA7,#00D4FF)', borderRadius: 6,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#fff', fontFamily: F.head }}>
              {'\u03A9'}
            </div>
            {!isMobile && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 800, color: T.cardText, letterSpacing: '-.02em', lineHeight: 1 }}>
                  OMNI<span style={{ color: '#00D4FF' }}>VIEW</span>
                </div>
                <div style={{ fontSize: 7, color: T.dim, textTransform: 'uppercase', letterSpacing: '.12em', fontFamily: F.mono }}>Agency Command</div>
              </div>
            )}
          </div>

          {/* Brand pills - scrollable on mobile */}
          {!isMobile && (
            <div style={{ display: 'flex', gap: 3, flexWrap: 'nowrap', overflowX: 'auto', flex: 1 }}>
              {allBrands.map(b => (
                <button key={b.id} data-testid={`brand-${b.id}`} onClick={() => switchBrand(b.id)}
                  style={{ padding: '5px 12px', borderRadius: 5, border: brand === b.id ? `1px solid ${b.col}` : `1px solid ${T.border}`,
                    background: brand === b.id ? `${b.col}18` : T.surfaceAlpha, color: brand === b.id ? b.col : T.muted,
                    fontSize: 12, cursor: 'pointer', fontFamily: F.mono, whiteSpace: 'nowrap', fontWeight: brand === b.id ? 600 : 400 }}>{b.name}</button>
              ))}
            </div>
          )}

          {isMobile && (
            <select data-testid="brand-mobile-select" value={brand} onChange={e => switchBrand(e.target.value)}
              style={{ flex: 1, padding: '6px 10px', borderRadius: 5, border: `1px solid ${activeBrand.col}`,
                background: T.surfaceAlpha, color: activeBrand.col, fontSize: 11, fontFamily: F.mono, appearance: 'auto' }}>
              {allBrands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}

          {/* Date range */}
          <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
            {['7d', '30d', '90d', 'YTD', '12m'].map(d => (
              <button key={d} data-testid={`dr-${d}`} onClick={() => switchDr(d)}
                style={{ padding: '4px 10px', borderRadius: 4, border: dr === d ? '1px solid #00D4FF55' : `1px solid ${T.border}`,
                  background: dr === d ? '#00D4FF12' : 'transparent', color: dr === d ? '#00D4FF' : T.dim,
                  fontSize: 11, cursor: 'pointer', fontFamily: F.mono, fontWeight: dr === d ? 600 : 400 }}>{d}</button>
            ))}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 5, flexShrink: 0, alignItems: 'center' }}>
            <button data-testid="toggle-theme" onClick={toggleTheme}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 6,
                border: `1px solid ${T.border}`, background: T.surfaceAlpha, color: T.muted, cursor: 'pointer' }}>
              {isDark ? <Sun size={15} /> : <Moon size={15} />}
            </button>
            {!isMobile && (
              <>
                <button data-testid="open-intelligence" onClick={() => setShowIntel(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 6,
                    border: '1px solid rgba(0,212,255,.45)', background: 'rgba(0,212,255,.1)', color: isDark ? '#00D4FF' : '#0369a1',
                    fontSize: 12, cursor: 'pointer', fontFamily: F.mono, fontWeight: 700 }}>
                  <Sparkles size={13}/> Ask AI
                </button>
                <button data-testid="open-sheets" onClick={() => setShowSheets(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 5,
                    border: '1px solid rgba(15,157,88,.35)', background: 'rgba(15,157,88,.1)', color: '#0F9D58',
                    fontSize: 12, cursor: 'pointer', fontFamily: F.mono, fontWeight: 500 }}>Sheets</button>
                <button data-testid="open-share" onClick={() => setShowShare(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 5,
                    border: '1px solid rgba(37,211,102,.3)', background: 'rgba(37,211,102,.08)', color: '#25D366',
                    fontSize: 12, cursor: 'pointer', fontFamily: F.mono, fontWeight: 500 }}>Share</button>
                <button data-testid="open-client" onClick={() => setShowClient(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 5,
                    border: '1px solid rgba(255,209,102,.3)', background: clientMode ? 'rgba(255,209,102,.12)' : 'rgba(255,209,102,.05)',
                    color: AMBER, fontSize: 12, cursor: 'pointer', fontFamily: F.mono, fontWeight: 500 }}>Client</button>
                <button data-testid="omniplan-btn" onClick={() => navigate('/omniplan')}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 6,
                    border: '1px solid rgba(255,209,102,.4)', background: 'rgba(255,209,102,.1)', color: AMBER,
                    fontSize: 12, cursor: 'pointer', fontFamily: F.mono, fontWeight: 700 }}>
                  <FileText size={13}/> OmniPlan
                </button>
                <button data-testid="agent-setup-btn" onClick={() => navigate('/agent/setup')}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 6,
                    border: '1px solid rgba(6,214,160,.4)', background: 'rgba(6,214,160,.1)', color: '#06D6A0',
                    fontSize: 12, cursor: 'pointer', fontFamily: F.mono, fontWeight: 700 }}>
                  <Bot size={13}/> OmniClaw
                </button>
              </>
            )}
            {isMobile && (
              <button data-testid="mobile-menu-toggle" onClick={() => setShowMobileMenu(!showMobileMenu)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 6,
                  border: `1px solid ${T.border}`, background: T.surfaceAlpha, color: T.muted, cursor: 'pointer' }}>
                <Menu size={15} />
              </button>
            )}
            <button data-testid="sources-toggle" onClick={() => setShowSources(s => !s)}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 10px', borderRadius: 5,
                border: `1px solid ${T.border}`, background: T.surfaceAlpha, color: '#06D6A0', fontSize: 11, cursor: 'pointer', fontFamily: F.mono }}>
              <Dot ok={!loading} col={loading ? '#FFD166' : '#06D6A0'} />
              <span style={{ fontWeight: 600 }}>{liveSourceCount}/{SOURCES.length}</span>
            </button>
            {!isMobile && (
              <button data-testid="logout-btn" onClick={async () => { await logout(); navigate('/'); }}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 6,
                  border: '1px solid rgba(255,107,107,.3)', background: 'rgba(255,107,107,.06)', color: '#FF6B6B',
                  fontSize: 12, cursor: 'pointer', fontFamily: F.mono, fontWeight: 600 }}>
                <LogOut size={13}/> Sign Out
              </button>
            )}
          </div>
        </div>

        {/* Mobile menu dropdown */}
        {isMobile && showMobileMenu && (
          <div className="animate-fadeUp" style={{ borderTop: `1px solid ${T.border}`, padding: '10px 0', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <button data-testid="mobile-omniplan" onClick={() => { navigate('/omniplan'); setShowMobileMenu(false); }}              style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid rgba(255,209,102,.4)',
                background: 'rgba(255,209,102,.1)', color: AMBER, fontSize: 12, cursor: 'pointer', fontFamily: F.mono, fontWeight: 700,
                display: 'flex', alignItems: 'center', gap: 5 }}>
              <FileText size={13}/> OmniPlan
            </button>
            <button data-testid="mobile-omniclaw" onClick={() => { navigate('/agent/setup'); setShowMobileMenu(false); }}
              style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid rgba(6,214,160,.4)',
                background: 'rgba(6,214,160,.1)', color: '#06D6A0', fontSize: 12, cursor: 'pointer', fontFamily: F.mono, fontWeight: 700,
                display: 'flex', alignItems: 'center', gap: 5 }}>
              <Bot size={13}/> OmniClaw
            </button>
            <button data-testid="mobile-sheets" onClick={() => { setShowSheets(true); setShowMobileMenu(false); }}
              style={{ padding: '8px 14px', borderRadius: 5, border: '1px solid rgba(15,157,88,.35)',
                background: 'rgba(15,157,88,.1)', color: '#0F9D58', fontSize: 12, cursor: 'pointer', fontFamily: F.mono }}>Sheets</button>
            <button data-testid="mobile-share" onClick={() => { setShowShare(true); setShowMobileMenu(false); }}
              style={{ padding: '8px 14px', borderRadius: 5, border: '1px solid rgba(37,211,102,.3)',
                background: 'rgba(37,211,102,.08)', color: '#25D366', fontSize: 12, cursor: 'pointer', fontFamily: F.mono }}>Share</button>
            <button data-testid="mobile-client" onClick={() => { setShowClient(true); setShowMobileMenu(false); }}
              style={{ padding: '8px 14px', borderRadius: 5, border: '1px solid rgba(255,209,102,.3)',
                background: 'rgba(255,209,102,.05)', color: AMBER, fontSize: 12, cursor: 'pointer', fontFamily: F.mono }}>Client</button>
            <button data-testid="mobile-logout" onClick={async () => { await logout(); navigate('/'); }}
              style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid rgba(255,107,107,.3)',
                background: 'rgba(255,107,107,.06)', color: '#FF6B6B', fontSize: 12, cursor: 'pointer', fontFamily: F.mono, fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 5 }}>
              <LogOut size={13}/> Sign Out
            </button>
          </div>
        )}

        {/* Sources bar - OAuth connect/manage/delete */}
        {showSources && (
          <div className="animate-fadeUp" style={{ borderTop: `1px solid ${T.border}`, padding: '10px 0 12px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <span style={{ fontSize:12, fontFamily:F.mono, fontWeight:700, color:T.text }}>Platform Connections</span>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <button data-testid="manage-connections-link" onClick={() => navigate('/connections')}
                  style={{ padding:'3px 8px', borderRadius:4, border:'1px solid rgba(0,47,167,.35)', background:'rgba(0,47,167,.08)',
                    color:'#4d7dff', fontSize:10, cursor:'pointer', fontFamily:F.mono, fontWeight:700 }}>
                  Manage real connections
                </button>
                <span style={{ fontSize:11, fontFamily:F.mono, color:T.muted }}>{SOURCES.filter(s=>s.ok).length} active</span>
              </div>
            </div>
            {/* OAuth Connectors - top row */}
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:8 }}>
              {[{id:'google_ads',name:'Google Ads',col:'#4285F4'},{id:'meta_ads',name:'Meta Ads',col:'#1877F2'},{id:'ga4',name:'GA4',col:'#E8710A'},{id:'google_sheets',name:'Google Sheets',col:'#0F9D58'}].map(p => {
                const conn = oauthConns.find(c => c.provider === p.id);
                return (
                  <div key={p.id} data-testid={`oauth-${p.id}`}
                    style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 12px', borderRadius:6, cursor:'pointer',
                      background:conn?`${p.col}12`:T.surfaceAlpha, border:`1px solid ${conn?p.col+'40':T.border}`, transition:'all .15s' }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow='0 2px 8px rgba(0,0,0,0.1)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow='none'}>
                    <Dot ok={!!conn} col={p.col}/>
                    <span style={{ fontSize:12, fontFamily:F.mono, fontWeight:600, color:conn?T.text:T.muted }}>{p.name}</span>
                    {conn ? (
                      <>
                        <span style={{ fontSize:9, color:'#06D6A0', fontFamily:F.mono, fontWeight:600 }}>LIVE</span>
                        <button data-testid={`disconnect-${p.id}`}
                          onClick={async (e) => { e.stopPropagation();
                            if (window.confirm(`Disconnect ${p.name}?`)) {
                              await axios.post(`${API}/api/oauth/${p.id}/disconnect`, {}, { withCredentials:true });
                              setOauthConns(c => c.filter(x => x.provider !== p.id));
                            }}}
                          style={{ padding:'2px 6px', borderRadius:3, border:'1px solid rgba(255,68,68,.3)', background:'rgba(255,68,68,.06)',
                            color:'#FF6B6B', fontSize:9, cursor:'pointer', fontFamily:F.mono, display:'flex', alignItems:'center', gap:2 }}>
                          <Unlink size={10}/> Disconnect
                        </button>
                      </>
                    ) : (
                      <button data-testid={`connect-${p.id}`}
                        onClick={async (e) => { e.stopPropagation();
                          let backendOk = false;
                          try {
                            const { data:d } = await axios.get(`${API}/api/oauth/${p.id}/url`, { withCredentials:true });
                            // Direct callback (simulated OAuth - in production this would redirect)
                            await axios.post(`${API}/api/oauth/${p.id}/callback`, { state:d.state, code:'demo_code' }, { withCredentials:true });
                            const { data:conns } = await axios.get(`${API}/api/oauth/connections`, { withCredentials:true });
                            setOauthConns(conns);
                            backendOk = true;
                          } catch(err) {
                            console.warn('OAuth connect API unavailable, using demo mode:', err?.message || err);
                            // Optimistic demo connection so the UX continues to feel real.
                            setOauthConns(prev => [...prev.filter(x => x.provider !== p.id), { provider:p.id, status:'connected', demo:true }]);
                          }
                          toast.success(`${p.name} connected`, {
                            description: backendOk ? 'Streaming live metrics now.' : 'Demo connection — dashboard is showing sample data.',
                          });
                        }}
                        style={{ padding:'2px 8px', borderRadius:3, border:`1px solid ${p.col}40`, background:`${p.col}12`,
                          color:p.col, fontSize:9, cursor:'pointer', fontFamily:F.mono, fontWeight:600, display:'flex', alignItems:'center', gap:2 }}>
                        <Link2 size={10}/> Connect
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Other sources */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {SOURCES.filter(s => !['Google Ads','Meta Ads','GA4','Google Sheets'].includes(s.name.split('/')[0].trim())).map(s => (
                <div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 4,
                  background: s.ok ? `${s.col}10` : T.surfaceAlpha, border: `1px solid ${s.ok ? s.col + '30' : T.border}` }}>
                  <Dot ok={s.ok} col={s.col} />
                  <span style={{ fontSize: 10, color: s.ok ? T.text : T.dim, fontFamily: F.mono }}>{s.name}</span>
                  <span style={{ fontSize: 8, color: T.muted, fontFamily: F.mono }}>{s.ok ? s.sync : 'OFF'}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Desktop Tab nav */}
        {!isMobile && (
          <nav role="tablist" aria-label="Dashboard modules" style={{ display: 'flex', gap: 0, borderTop: `1px solid ${T.border}` }}>
            {TABS.map(t => {
              const Icon = ICON_MAP[t.icon];
              return (
                <Link key={t.id} data-testid={`tab-${t.id}`} to={dashPath(brand, t.id)}
                  role="tab" aria-selected={tab === t.id} aria-label={t.label}
                  style={{ padding: '10px 16px', border: 'none', background: 'transparent', textDecoration: 'none', color: tab === t.id ? T.cardText : T.muted,
                    fontSize: 13, cursor: 'pointer', fontFamily: F.head, fontWeight: tab === t.id ? 700 : 500,
                    borderBottom: tab === t.id ? `2px solid ${activeBrand.col}` : '2px solid transparent',
                    transition: 'all .15s', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}>
                  {Icon && <Icon size={13} />}{t.label}
                </Link>
              );
            })}
          </nav>
        )}
      </header>

      {/* Client mode bar */}
      {clientMode && (
        <div style={{ background: 'rgba(255,107,107,.06)', borderBottom: '1px solid rgba(255,107,107,.15)',
          padding: '6px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 10, color: '#FF6B6B', fontFamily: F.mono }}>CLIENT VIEW ACTIVE</span>
          <span style={{ fontSize: 9, color: T.muted, fontFamily: F.mono }}>Hidden sections not visible</span>
        </div>
      )}

      {/* Brand bar */}
      <div style={{ padding: '10px 16px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 14 }}>
        <span style={{ width: 4, height: 32, background: activeBrand.col, borderRadius: 2, flexShrink: 0,
          boxShadow: `0 0 10px ${activeBrand.col}66` }} />
        <div style={{ flex: 1 }}>
          <h1 data-testid="brand-heading" style={{ fontSize: isMobile ? 16 : 20, fontWeight: 800, color: T.cardText, letterSpacing: '-.02em', margin: 0 }}>{activeBrand.name}</h1>
          <div style={{ fontSize: 12, color: T.muted, fontFamily: F.mono, fontWeight: 500 }}>
            {activeBrand.ind} {'\u00B7'} {activeBrand.market} {'\u00B7'} {activeBrand.currency} {'\u00B7'} {dr}
          </div>
          {activeBrand.kind === 'portfolio' && (
            <div data-testid="fx-note" style={{ fontSize: 11, color: T.dim, fontFamily: F.mono, marginTop: 2 }}>
              Converted to EUR at 1 EUR = 92.5 INR {'\u00B7'} 1.08 USD {'\u00B7'} 0.85 GBP on {FX_AS_OF}
            </div>
          )}
        </div>
        {!isMobile && (
          <div style={{ fontSize: 9, color: T.dim, fontFamily: F.mono }}>
            {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
          </div>
        )}
      </div>

      {/* Content */}
      {sub && sub.status !== 'active' && (
        <div data-testid="trial-banner" style={{
          background: sub.days_remaining > 2 ? 'rgba(0,47,167,.07)' : 'rgba(255,159,67,.11)',
          borderBottom: `1px solid ${T.border}`, padding: '9px 20px', fontSize: 13,
        }}>
          <div style={{ maxWidth: 1640, margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
            <span style={{ color: T.text }}>
              {sub.days_remaining > 0
                ? `${sub.days_remaining} day${sub.days_remaining === 1 ? '' : 's'} of access left${sub.status === 'extended_credit' ? ', including your referral credit' : ' on your free trial'}.`
                : 'Your free trial has ended.'}
              {sub.credit_days_balance > 0 && ` You have ${sub.credit_days_balance} referral days waiting.`}
            </span>
            <Link to="/billing" data-testid="trial-banner-billing"
              style={{ color: '#002FA7', fontWeight: 600, textDecoration: 'none' }}>
              {sub.credit_days_balance > 0 ? 'Apply your free days' : 'Choose a plan'} {'\u2192'}
            </Link>
          </div>
        </div>
      )}

      <main id="main" aria-busy={loading} style={{ padding: isMobile ? '14px 12px 100px' : '20px', maxWidth: 1640, margin: '0 auto' }}>
        <h2 style={{ position:'absolute', width:1, height:1, overflow:'hidden', clip:'rect(0 0 0 0)', whiteSpace:'nowrap' }}>
          {activeTab.label} — {activeBrand.name}
        </h2>
        {loading && (
          <div data-testid="dashboard-skeleton" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="kpi-grid">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="skeleton" style={{ height: 128, animationDelay: `${i * 60}ms` }} />
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
              <div className="skeleton" style={{ height: 260 }} />
              <div className="skeleton" style={{ height: 260 }} />
            </div>
          </div>
        )}
        {!loading && tab === 'overview' && <OverviewTab data={data} />}
        {!loading && tab === 'paid' && <PaidTab data={data} />}
        {!loading && tab === 'ecom' && <EcomTab data={data} />}
        {!loading && tab === 'organic' && <OrganicTab data={data} />}
        {!loading && tab === 'crm' && <CRMTab data={data} />}
        {!loading && tab === 'comp' && <CompTab data={data} />}
        {!loading && tab === 'blindspots' && <BlindspotsTab data={data} />}
      </main>

      {/* Mobile Bottom Tab Bar */}
      {isMobile && (
        <div className="mobile-tab-bar" data-testid="mobile-tab-bar"
          style={{ background: isDark ? 'rgba(7,9,15,0.88)' : 'rgba(255,255,255,0.88)',
            borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-around', padding: '6px 0 env(safe-area-inset-bottom, 8px)' }}>
          {TABS.map(t => {
            const Icon = ICON_MAP[t.icon];
            const active = tab === t.id;
            return (
              <Link key={t.id} data-testid={`mobile-tab-${t.id}`} to={dashPath(brand, t.id)} aria-label={t.label}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '4px 6px',
                  border: 'none', background: active ? `${activeBrand.col}14` : 'transparent', borderRadius: 8,
                  textDecoration: 'none',
                  color: active ? activeBrand.col : T.muted, cursor: 'pointer', minWidth: 44, transition: 'all .15s' }}>
                {Icon && <Icon size={18} strokeWidth={active ? 2.5 : 1.5} />}
                <span style={{ fontSize: 8, fontFamily: F.mono, fontWeight: active ? 600 : 400 }}>{t.mobileLabel}</span>
              </Link>
            );
          })}
        </div>
      )}

      {/* Footer - desktop only */}
      {!isMobile && (
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontSize: 9, color: T.dim, fontFamily: F.mono }}>
            OmniView Agency Command {'\u00B7'} {SOURCES.filter(s => s.ok).length} of {SOURCES.length} sources connected {'\u00B7'} {TABS.length} modules
          </div>
          <div style={{ fontSize: 9, color: T.dim, fontFamily: F.mono }}>
            Powered by open source AI {'\u00B7'} Qwen 3.6 {'\u00B7'} Gemma 4
          </div>
        </div>
      )}

      {/* Modals */}
      {showSheets && <SheetsPanel onClose={() => setShowSheets(false)} />}
      {showShare && <ShareModal data={data} brand={activeBrand} dateRange={dr} shareUrl={typeof window !== 'undefined' ? window.location.href : ''} onClose={() => setShowShare(false)} />}
      {showClient && <ClientPanel visible={clientVis} onToggle={toggleCV} onClose={() => setShowClient(false)} />}
    </div>
  );
}
